import { ReactNode, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { dayjs } from '@umanforge/common';
import { DotsIcon } from '../icons/dots.icon';


export interface DriveCardAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

interface DriveCardShellProps {
  icon: ReactNode;
  name: string;
  subtitle: string;
  lastModified?: Date | string;
  actions?: DriveCardAction[];
  onClick?: () => void;
}

function formatDate( date: Date | string | undefined, t: ( k: string ) => string ): string {
  if ( !date ) {
    return '';
  }
  const d = dayjs( date );
  if ( !d.isValid() ) {
    return '';
  }
  return t( 'translation:modified_on' ) + ' ' + d.format( 'DD/MM/YYYY' );
}

export function DriveCardShell( { icon, name, subtitle, lastModified, actions, onClick }: DriveCardShellProps ) {
  const { t } = useTranslation();
  const [ menuOpen, setMenuOpen ] = useState( false );
  const menuRef = useRef<HTMLDivElement>( null );

  useEffect( () => {
    if ( !menuOpen ) {
      return;
    }
    const handleClickOutside = ( e: MouseEvent ) => {
      if ( menuRef.current && !menuRef.current.contains( e.target as Node ) ) {
        setMenuOpen( false );
      }
    };
    document.addEventListener( 'mousedown', handleClickOutside );
    return () => { document.removeEventListener( 'mousedown', handleClickOutside ); };
  }, [ menuOpen ] );

  return (
    <div
      className={ `relative flex flex-col items-center justify-between w-40 h-48 p-4 pt-2 bg-white rounded-xl shadow-md border border-gray-100 select-none hover:shadow-lg transition-shadow duration-150 ${ onClick ? 'cursor-pointer' : '' }` }
      onClick={ onClick ? onClick : undefined }
    >
      {/* 3-dot menu */}
      { actions && actions.length > 0 && (
        <div
          ref={ menuRef }
          className="absolute top-2 right-2"
          onClick={ e => { e.stopPropagation(); } }
        >
          <button
            type="button"
            className="flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            onClick={ () => { setMenuOpen( prev => { return !prev; } ); } }
          >
            <DotsIcon className="w-3 h-5 fill-current" />
          </button>
          { menuOpen && (
            <div className="absolute flex flex-col divide-y mx-3 right-0 top-8 z-50 min-w-[180px] bg-white rounded-xl shadow-2xl border border-gray-100 justify-center overflow-hidden">
              { actions.map( ( action, idx ) => {
                return (
                  <button
                    key={ idx }
                    type="button"
                    className={ `flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm hover:bg-gray-50 transition-colors ${ action.danger ? 'text-red-500' : 'text-gray-700' }` }
                    onClick={ () => { action.onClick(); setMenuOpen( false ); } }
                  >
                    { action.icon && (
                      <span className={ `flex items-center justify-center w-5 h-5 shrink-0 ${ action.danger ? 'text-red-500' : 'text-gray-500' }` }>
                        { action.icon }
                      </span>
                    ) }
                    <span>{ action.label }</span>
                  </button>
                );
              } ) }
            </div>
          ) }
        </div>
      ) }

      {/* Icon */}
      <div className="flex flex-1 items-center justify-center w-full">
        { icon }
      </div>

      {/* Info */}
      <div className="flex flex-col items-start w-full gap-0.5">
        <span className="text-sm font-semibold text-gray-800 w-full truncate">{ name }</span>
        <span className="text-xs text-gray-400">{ subtitle }</span>
        { lastModified && (
          <span className="text-xs text-gray-300 mt-0.5">{ formatDate( lastModified, t ) }</span>
        ) }
      </div>
    </div>
  );
}
